import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import databaseService from '../services/DatabaseService';
import './ResultsPage.css';

const ResultsPage = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const questionnaireData = localStorage.getItem('questionnaireData');
        
        if (!questionnaireData) {
          navigate('/cataract-lens-questionnaire');
          return;
        }
        
        await databaseService.initialize();
        const parsedData = JSON.parse(questionnaireData);
        const results = databaseService.getLensRecommendations(parsedData);
        
        setRecommendations(results);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setError('Failed to load recommendations. Please try again.');
        setLoading(false);
      }
    };
    
    fetchRecommendations();
  }, [navigate]);

  const handlePremiumReportClick = () => navigate('/premium-report');

  if (loading) {
    return (
      <div className="results-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Our clinical engine is analyzing your 9-question profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-page">
        <div className="container">
          <div className="error-container card">
            <h2>Analysis Error</h2>
            <p>{error}</p>
            <Link to="/cataract-lens-questionnaire" className="btn btn-primary">
              Restart Assessment
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="results-page">
      <div className="container">
        <header className="results-intro">
          <h1 className="section-title">Your Lens Analysis</h1>
          <p className="section-subtitle">Based on your clinical profile, we've identified the following lens technologies as your best matches.</p>
        </header>

        <section className="premium-upsell-section" style={{ marginBottom: '2rem' }}>
          <div className="premium-upsell-card">
            <div className="upsell-content">
              <h2>Ready to talk to your surgeon?</h2>
              <p>Download your comprehensive <strong>Professional Clinical Report</strong>. Includes detailed pros/cons, exact questions to ask your doctor, cost estimates, surgery prep, and recovery timelines.</p>
              <ul className="upsell-features">
                <li>Patient Profile Summary</li>
                <li>Detailed Clinical Reasoning (Pros/Cons)</li>
                <li>10 Doctor Consultation Questions</li>
                <li>Cost, Pricing, and Recovery Timelines</li>
              </ul>
              <button className="btn btn-primary btn-large" onClick={handlePremiumReportClick} style={{ marginTop: '1rem' }}>
                Get Professional Report ($5)
              </button>
            </div>
            <div className="upsell-visual">
              <div className="pdf-preview-box">PREMIUM PDF REPORT</div>
            </div>
          </div>
        </section>

        <div className="recommendations-grid">
          {recommendations.map((lens, index) => (
            <div className="recommendation-card card" key={lens.id}>
              <div className="recommendation-header">
                <div>
                  <span className="rank-badge">#{index + 1} Recommendation</span>
                  <h2 className="recommendation-title">{lens.name}</h2>
                  <span className="lens-type-tag">{lens.type}</span>
                </div>
                <div className="match-score">
                  <div className="match-percentage">{lens.matchPercentage}% Match</div>
                </div>
              </div>
              
              <p className="recommendation-description">{lens.description}</p>
              
              <div className="vision-ranges-section">
                <h4>Vision Coverage</h4>
                <div className="vision-pills">
                  <span className={`pill ${lens.visionRanges.distance ? 'active' : ''}`}>Distance</span>
                  <span className={`pill ${lens.visionRanges.intermediate ? 'active' : ''}`}>Computer</span>
                  <span className={`pill ${lens.visionRanges.near ? 'active' : ''}`}>Reading</span>
                </div>
              </div>

              <div className="pros-cons-mini">
                <div className="pros">
                  {lens.prosAndCons.pros.slice(0, 2).map((pro, i) => (
                    <div key={i} className="pro-item">✓ {pro}</div>
                  ))}
                </div>
              </div>

              <div className="recommendation-actions">
                <button 
                  className="btn btn-secondary btn-sm"
                  onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(lens.name)}+cataract+lens+reviews`, '_blank')}
                >
                  View Reviews
                </button>
                <a href={lens.manufacturerUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm">
                  Official Site
                </a>
              </div>
            </div>
          ))}
        </div>



        <section className="comparison-table-section">
          <h2 className="section-title">Technical Comparison</h2>
          <div className="table-responsive card">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Lens System</th>
                  <th>Category</th>
                  <th>Match</th>
                  <th>Distance</th>
                  <th>Inter.</th>
                  <th>Near</th>
                  <th>Artifacts</th>
                </tr>
              </thead>
              <tbody>
                {recommendations.map((lens) => (
                  <tr key={lens.id}>
                    <td className="font-bold">{lens.name}</td>
                    <td>{lens.type}</td>
                    <td className="match-cell">{lens.matchPercentage}%</td>
                    <td>{lens.visionRanges.distance ? '●' : '○'}</td>
                    <td>{lens.visionRanges.intermediate ? '●' : '○'}</td>
                    <td>{lens.visionRanges.near ? '●' : '○'}</td>
                    <td>{lens.artifactProfile}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ResultsPage;
